import { POST_PROCESSING, urlSyncSettings } from './urlSyncSettings';
import { URL_PARAMS } from './urlParamNames';
import moment from 'moment';
import {
   CHRONOLOGICAL_DATE_TIME_FORMAT_DASHED,
   TROUBLE_INTERVALS_MAP,
} from '../components/widgets/troubleshooting/constants';
import * as browserStorage from '../misc/BrowserStorageManager';
import { TOAST_TYPES } from '../constants/general';
import { initialState } from '../redux/reducers/generalReducer';

function isEqualToInitial(current, initial) {
   if (moment.isMoment(current) && moment.isMoment(initial)) return current.isSame(initial);
   if (Array.isArray(current)) return JSON.stringify(current) === JSON.stringify(initial);
   if (current !== null && typeof current === 'object') return JSON.stringify(current) === JSON.stringify(initial);
   return current === initial;
}

let justReloaded = false;

// store -> URL
export function syncURL(pathname, pushToUrl, currentState) {
   const viewPageName = pathname.substr(1);
   const isUserAdmin = isAdminUserLoggedIn();
   // console.log('pushToUrl :>> ', pushToUrl);

   let urlParams = `${pathname}?`;

   Object.keys(urlSyncSettings).forEach((param) => {
      const { urlFn, group, storeName, postProcessing, views, alwaysSync } = urlSyncSettings[param];

      const isOriginRelatedParam = param === URL_PARAMS.ORIGIN_HASH || param === URL_PARAMS.ORIGIN_NAME;

      if (isOriginRelatedParam) {
         if (!isUserAdmin && isUserInfoAvailable()) {
            const orgaInfo = getUserOrga();
            urlParams += urlFn(param, orgaInfo[param]);
         }
         return;
      }

      const isOrgaRelatedParam = param === URL_PARAMS.ORGA_HASH || param === URL_PARAMS.ORGA_NAME;

      if (isOrgaRelatedParam && !isUserAdmin) {
         return;
      }
      const currentViewHasNotParam = views && views.every((viewName) => viewPageName !== viewName);
      if (currentViewHasNotParam) {
         return;
      }
      let currentStateValue = group !== '' ? currentState[group][storeName] : currentState[storeName];
      // console.log('currentStateValue :>> ', currentStateValue);
      currentStateValue = currentStateValue === undefined ? '' : currentStateValue;
      // console.log('currentStateValue 2:>> ', currentStateValue);

      let paramValue = pushToUrl[param] !== undefined ? pushToUrl[param] : currentStateValue;

      // if (pushToUrl[storeName] !== undefined) {
      //    console.log('group :>> ', group);
      //    console.log('storeName :>> ', storeName);
      //    console.log('pushToUrl[param] :>> ', pushToUrl[param]);
      //    console.log('currentStateValue :>> ', currentStateValue);
      // }
      // console.log('param :>> ', param);
      // console.log('paramValue :>> ', paramValue);
      // console.log('postProcessing :>> ', postProcessing);
      // console.log('isUserAdmin :>> ', isUserAdmin);

      if (!alwaysSync) {
         const initialValue = group !== '' ? initialState[group]?.[storeName] : initialState[storeName];
         if (isEqualToInitial(paramValue, initialValue)) {
            return;
         }
      }

      if (postProcessing !== undefined) {
         if (postProcessing === POST_PROCESSING.MOMENT_CONVERSION) {
            paramValue = paramValue.format(CHRONOLOGICAL_DATE_TIME_FORMAT_DASHED); //  moment.utc(timespan.gte.format());
         }
         if (postProcessing === POST_PROCESSING.EXTRACT_INTERVAL_VALUE) {
            paramValue = paramValue.value;
         }
      }

      // console.log('param 2:>> ', param);
      // console.log('paramValue 2:>> ', paramValue);
      // console.log('paramValue :>> ', paramValue);
      urlParams += urlFn(param, paramValue);
   });

   urlParams = removeCertainLastChar(urlParams, '&');
   // console.log('urlParams :>> ', urlParams);
   return urlParams;
}

// URL -> store
export function syncStore(queryURL, newView, store, firstRendering, currentView) {
   const newState = { ...store };
   const isUserAdmin = isAdminUserLoggedIn();
   // console.log('orgaHash :>> ', orgaHash);
   // create new refs one level deeper
   const groups = new Set();
   Object.values(urlSyncSettings).forEach((syncSetting) => {
      if (syncSetting.group) {
         const groupName = syncSetting.group;
         // console.log('groupName => ', groupName);
         groups.add(groupName);
      }
   });

   groups.forEach((groupName) => {
      newState[groupName] = { ...store[groupName] };
   });

   try {
      const splitUrlArray = queryURL.split('?');

      if (splitUrlArray.length > 2 || splitUrlArray.length === 0) {
         throw Error('"?" number of findings !== 1 || 2');
      }
      if (splitUrlArray.length === 1) {
         // console.log('splitUrlArray.length === 1 ');
         return newState;
      }

      const paramUrl = splitUrlArray[1];
      const params = paramUrl.split('&');
      // console.log('params :>> ', params);

      // when a copied troubleshooting URL is inserted into the browser URL input
      // while the troubleshooting page is already open (and has state)
      // a reload is needed to clear the page before the URL state can be applied
      if (newView === 'troubleshooting' && currentView === 'troubleshooting') {
         if (justReloaded === false) {
            window.location.reload();
            justReloaded = true;
         } else {
            justReloaded = false;
         }
      }

      const splitParamMap = {};
      params.forEach((param) => splitParamKeyValue(param, splitParamMap));
      // console.log('splitParamMap :>> ', splitParamMap);

      Object.keys(urlSyncSettings).forEach((urlParamName, idx) => {
         // console.log('urlParamName :>> ', urlParamName);
         const { parseFn, group, storeName, postProcessing, views, adminTargetProp } = urlSyncSettings[urlParamName];

         const isOriginRelatedParam =
            urlParamName === URL_PARAMS.ORIGIN_HASH || urlParamName === URL_PARAMS.ORIGIN_NAME;

         if (isOriginRelatedParam) {
            // origin information is not stored in store -> source is session storage / userInfo
            if (!isUserInfoAvailable()) {
               // non admin URL -> use with admin acccount: set orga origin to orga filter
               if (newState['filter'][adminTargetProp] === '') {
                  // only set orga filter via origin when orga filter has not been set before
                  newState['filter'][adminTargetProp] = parseFn(splitParamMap[urlParamName]);
               }
            }
            return;
         }

         const isOrgaRelatedParam = urlParamName === URL_PARAMS.ORGA_HASH || urlParamName === URL_PARAMS.ORGA_NAME;

         // console.log('isUserAdmin :>> ', isUserAdmin);
         // console.log('isUserInfoAvailable :>> ', isUserInfoAvailable());
         if (isUserInfoAvailable() && isOrgaRelatedParam && !isUserAdmin) return;

         const currentViewHasNotParam = views && views.every((viewName) => newView !== viewName);
         if (currentViewHasNotParam) {
            return;
         }

         const paramFound = urlParamName in splitParamMap;

         // console.log('splitParamMap[urlParamName] :>> ', splitParamMap[urlParamName]);
         // console.log('urlParamName :>> ', urlParamName);
         // console.log('paramFound :>> ', paramFound);

         if (paramFound) {
            let newValue = parseFn(splitParamMap[urlParamName]);

            if (postProcessing !== undefined) {
               if (postProcessing === POST_PROCESSING.MOMENT_CONVERSION) {
                  newValue = moment.utc(newValue, CHRONOLOGICAL_DATE_TIME_FORMAT_DASHED); //  moment.utc(timespan.gte.format());
               }
               if (postProcessing === POST_PROCESSING.EXTRACT_INTERVAL_VALUE) {
                  newValue = TROUBLE_INTERVALS_MAP[newValue];
               }
            }

            if (group === '') {
               newState[storeName] = newValue;
            } else {
               newState[group][storeName] = newValue;
            }
         }

         // console.log('newValue :>> ', newValue);
         // console.log('group :>> ', group);
         // console.log('storeName :>> ', storeName);
      });
   } catch (error) {
      // console.error('error:>> ', error);
      return Object.assign({}, store, {
         alarm: {
            type: TOAST_TYPES.ERROR,
            message: error.message + ', resetting page', // URL could not be parsed,
         },
      });
   }
   // console.log('URL ---> STORE: newState :>> ', newState);
   return newState;
}

function splitParamKeyValue(param, parsedParamMap) {
   if (param === 'refresh') {
      // param has no meaning, it is just used to make URL different to last one
      return;
   }
   const splitParamArray = param.split('=');
   // console.log('splitParamArray :>> ', splitParamArray);
   if (splitParamArray.length !== 2) {
      throw Error('Split Params !==2');
   }
   const [key, value] = splitParamArray;
   parsedParamMap[key] = value;
}

function removeCertainLastChar(str, lastChar) {
   const lastCharIdx = str.length - 1;
   if (str[lastCharIdx] === lastChar) {
      return str.substr(0, lastCharIdx);
   }
   return str;
}

function isAdminUserLoggedIn() {
   if (isUserInfoAvailable()) {
      const userInfo = browserStorage.getUserInfo();
      return userInfo.isAdmin;
   }
   return false;
}

function getUserOrga() {
   if (isUserInfoAvailable()) {
      const userInfo = browserStorage.getUserInfo();
      return {
         [URL_PARAMS.ORIGIN_HASH]: userInfo.organization.hash,
         [URL_PARAMS.ORIGIN_NAME]: userInfo.organization.name,
      };
   }
   return { [URL_PARAMS.ORIGIN_HASH]: 'unknown', [URL_PARAMS.ORIGIN_NAME]: 'unknown' };
}

function isUserInfoAvailable() {
   return browserStorage.getUserInfo() !== null;
}
